import * as React from 'react';
import { CheckIcon, ChevronsUpDown } from 'lucide-react';
import RPNInput from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import { parsePhoneNumber, getCountryCallingCode } from 'react-phone-number-input';
import type { Country, Value } from 'react-phone-number-input';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { cn } from '@/lib/utils';
import { getExampleNumber } from 'libphonenumber-js';
import examples from 'libphonenumber-js/examples.mobile.json';
import { ScrollArea } from './scroll-area';

type PhoneData = {
  countryCode: string;
  phoneNumber: string;
  country: Country | undefined;
  fullNumber: string;
};

type PhoneInputProps = Omit<React.ComponentProps<'input'>, 'onChange' | 'value'> & {
  value?: Value;
  defaultCountry?: Country;
  onChange?: (data: PhoneData) => void;
  disabled?: boolean;
};

const getMaxLengthForCountry = (country: Country | undefined): number => {
  if (!country) return 15;

  try {
    const exampleNumber = getExampleNumber(country, examples);

    if (exampleNumber) {
      const nationalNumber = exampleNumber.number;
      return nationalNumber.length + 3;
    }
  } catch (error) {
    console.error('Error getting max length:', error);
  }

  return 15;
};

const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ className, onChange, value, defaultCountry, ...props }, ref) => {
    const [country, setCountry] = React.useState<Country | undefined>(defaultCountry);
    const [maxLength, setMaxLength] = React.useState<number>(15);

    React.useEffect(() => {
      const newMaxLength = getMaxLengthForCountry(country);
      setMaxLength(newMaxLength);
    }, [country]);

    const handleChange = (val: Value) => {
      let phoneData: PhoneData = {
        countryCode: '',
        phoneNumber: '',
        country: country,
        fullNumber: val || '',
      };

      if (val) {
        try {
          const parsed = parsePhoneNumber(val);
          if (parsed) {
            phoneData = {
              countryCode: `+${parsed.countryCallingCode}`,
              phoneNumber: parsed.nationalNumber,
              country: parsed.country,
              fullNumber: val,
            };
            setCountry(parsed.country);
          }
        } catch (error) {
          // If parsing fails, still return the raw value
          if (country) {
            phoneData.countryCode = `+${getCountryCallingCode(country)}`;
          }
        }
      }

      onChange?.(phoneData);
    };

    return (
      <RPNInput
        // ref={ref}
        className={cn('flex flex-1', className)}
        flagComponent={FlagComponent}
        countrySelectComponent={CountrySelect}
        inputComponent={InputComponent}
        smartCaret={false}
        value={value}
        defaultCountry={defaultCountry || 'BD'}
        onChange={handleChange}
        onCountryChange={setCountry}
        international
        maxLength={maxLength}
        {...props}
      />
    );
  }
);
PhoneInput.displayName = 'PhoneInput';

const InputComponent = React.forwardRef<HTMLInputElement, React.ComponentProps<'input'>>(
  ({ className, ...props }, ref) => (
    <Input
      className={cn(
        '!px-2 !h-9 rounded-l-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0  placeholder:text-gray-400',
        className
      )}
      {...props}
      ref={ref}
    />
  )
);
InputComponent.displayName = 'InputComponent';

type CountryEntry = { label: string; value: Country | undefined };

type CountrySelectProps = {
  disabled?: boolean;
  value: Country;
  options: CountryEntry[];
  onChange: (country: Country) => void;
};

const CountrySelect = ({
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange,
}: CountrySelectProps) => {
  const scrollAreaRef = React.useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <Popover
      open={isOpen}
      modal
      onOpenChange={(open) => {
        setIsOpen(open);
        open && setSearchValue('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className="flex gap-1 rounded-r-none border-0 border-r border-gray-200 px-3 focus:z-10 bg-gradient-green bg-black"
          disabled={disabled}
        >
          <FlagComponent country={selectedCountry} countryName={selectedCountry} />
          <ChevronsUpDown
            className={cn('size-4 opacity-60', disabled ? 'hidden' : 'opacity-100')}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0">
        <Command>
          <CommandInput
            value={searchValue}
            onValueChange={(value) => {
              setSearchValue(value);
              setTimeout(() => {
                if (scrollAreaRef.current) {
                  const viewportElement = scrollAreaRef.current.querySelector(
                    '[data-radix-scroll-area-viewport]'
                  );
                  if (viewportElement) {
                    viewportElement.scrollTop = 0;
                  }
                }
              }, 0);
            }}
            placeholder="Search country..."
          />
          <CommandList>
            <ScrollArea ref={scrollAreaRef} className="h-72">
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {countryList.map(({ value, label }) =>
                  value ? (
                    <CountrySelectOption
                      key={value}
                      country={value}
                      countryName={label}
                      selectedCountry={selectedCountry}
                      onChange={onChange}
                      onSelectComplete={() => setIsOpen(false)}
                    />
                  ) : null
                )}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

type FlagProps = {
  country: Country;
  countryName: string;
};

interface CountrySelectOptionProps extends FlagProps {
  selectedCountry: Country;
  onChange: (country: Country) => void;
  onSelectComplete: () => void;
}

const CountrySelectOption = ({
  country,
  countryName,
  selectedCountry,
  onChange,
  onSelectComplete,
}: CountrySelectOptionProps) => {
  const handleSelect = () => {
    onChange(country);
    onSelectComplete();
  };

  return (
    <CommandItem className="gap-2" onSelect={handleSelect}>
      <FlagComponent country={country} countryName={countryName} />
      <span className="flex-1 text-sm">{countryName}</span>
      <span className="text-sm text-black">{`+${getCountryCallingCode(country)}`}</span>
      <CheckIcon
        className={`ml-auto size-4 ${country === selectedCountry ? 'opacity-100' : 'opacity-0'}`}
      />
    </CommandItem>
  );
};

const FlagComponent = ({ country, countryName }: FlagProps) => {
  const Flag = flags[country];

  return (
    <span className="flex h-4 w-6 overflow-hidden rounded-sm bg-foreground/20 [&_svg:not([class*='size-'])]:size-full">
      {Flag && <Flag title={countryName} />}
    </span>
  );
};

export type { PhoneData };

export default PhoneInput;
